import React, { useState, useEffect } from "react";
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Platform,
  TextInput,
  ScrollView,
  Dimensions,
} from "react-native";
import { useForm, Controller } from "react-hook-form";
import { Button } from "react-native-paper";
import { useDispatch, useSelector } from "react-redux";
import { useToast } from "react-native-toast-notifications";
import { useTranslation } from "../../../utils/useTranslation";
import {
  createFactoryRunShipment,
  getChinaTrackerShipmentDetailsByDetailId,
} from "../../../redux/dispatcher/chinaTrackerDispatcher";
import { DatePickerInput } from "react-native-paper-dates";

interface FactoryRunShipmentModalProps {
  visible: boolean;
  onClose: () => void;
  factoryOrderDetail: any;
}

interface FormData {
  shipmentQty: string;
  containerNo: string;
  shipmentNo: string;
  ETD: Date;
  duration: string;
  ETA?: Date;
  remarks?: string;
}

const FactoryRunShipmentModal: React.FC<FactoryRunShipmentModalProps> = ({
  visible,
  onClose,
  factoryOrderDetail,
}) => {
  const dispatch = useDispatch<any>();
  const toast = useToast();
  const { t } = useTranslation();
  const { orderDetailShipments, loadingShipmentsAction } = useSelector(
    (state: any) => state.chinaTracker
  );
  const [shipmentQtyWarnMsg, setShipmentQtyWarnMsg] = useState<string | null>(
    null
  );

  const nextShipmentNo = (orderDetailShipments?.length ?? 0) + 1 || 1;

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      shipmentQty: "",
      containerNo: "",
      shipmentNo: nextShipmentNo.toString(),
      ETD: new Date(),
      duration: "",
      ETA: new Date(),
      remarks: "",
    },
  });

  const onSubmit = async (data: FormData) => {
    const etd = data.ETD;
    const duration = Number(data.duration);

    const eta = new Date(etd);
    eta.setDate(etd.getDate() + duration);

    const payload = {
      factoryOrderDetail: factoryOrderDetail._id,
      shipmentQty: Number(data.shipmentQty.toString().replace(/,/g, "")),
      containerNo: data.containerNo,
      shipmentNo: Number(data.shipmentNo),
      ETD: data.ETD,
      duration: Number(data.duration),
      ETA: eta,
      remarks: data.remarks || "",
    };
    await dispatch(createFactoryRunShipment(payload))
      .unwrap()
      .then(() => {
        toast.show("Shipment created successfully", { type: "success" });
        onClose();
        dispatch(
          getChinaTrackerShipmentDetailsByDetailId(factoryOrderDetail._id)
        ).unwrap();
      })
      .catch((err: any) => {
        console.error("Error creating shipment:", err);
        toast.show(`Failed to create shipment: ${err}`, { type: "danger" });
      });
  };

  const renderDatePicker = (
    label: string,
    value: Date | undefined,
    onChange: (date: Date) => void,
    disabled?: boolean
  ) => (
    <View style={styles.field}>
      <Text style={styles.label}>{label}</Text>
      <View style={{ position: "relative" }}>
        {/* @ts-ignore: DatePickerInput typing conflict */}
        <DatePickerInput
          locale="en-GB"
          value={value instanceof Date ? value : undefined}
          onChange={(newDate: any) => {
            if (newDate && !disabled) onChange(newDate);
          }}
          inputMode="start"
          mode="outlined"
          withDateFormatInLabel={false}
          style={[
            styles.datePicker,
            disabled && { backgroundColor: "#f2f2f2" },
          ]}
        />

        {/* Overlay to block touches when disabled */}
        {disabled && (
          <View
            style={{
              ...StyleSheet.absoluteFillObject,
              backgroundColor: "transparent",
            }}
            pointerEvents="auto"
          />
        )}
      </View>
    </View>
  );

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === "duration" || name === "ETD") {
        const etd = value.ETD;
        const duration = Number(value.duration);

        if (etd instanceof Date && !isNaN(duration) && duration > 0) {
          const eta = new Date(etd);
          eta.setDate(etd.getDate() + duration);
          setValue("ETA", eta);
        } else {
          setValue("ETA", undefined);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, setValue]);

  return (
    <Modal visible={visible} transparent animationType="slide">
      <View style={styles.overlay}>
        <View style={styles.modalContainer}>
          <View style={styles.header}>
            <Text style={styles.title}>
              {t("createShipment")} - {factoryOrderDetail?.orderNumber}
            </Text>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              <Text style={styles.closeButtonText}>✕</Text>
            </TouchableOpacity>
          </View>

          <ScrollView showsVerticalScrollIndicator={false}>
            <Controller
              control={control}
              name="shipmentNo"
              render={({ field: { value } }) => (
                <View style={styles.field}>
                  <Text style={styles.label}>{t("shipmentNo")}</Text>
                  <TextInput
                    style={[styles.input, { backgroundColor: "#f1f1f1" }]}
                    value={value}
                    editable={false}
                  />
                </View>
              )}
            />

            <Controller
              control={control}
              name="shipmentQty"
              rules={{
                required: "Shipment quantity is required",
                validate: (value: any) => {
                  const plannedQty = factoryOrderDetail?.plannedQty || 0;
                  const existingPlannedQty =
                    orderDetailShipments?.reduce((sum: number, item: any) => {
                      return sum + (item?.shipmentQty || 0);
                    }, 0) || 0;

                  const maxAllowed = Math.max(
                    plannedQty - existingPlannedQty,
                    0
                  );

                  if (!value) return "Quantity is required";
                  if (value <= 0) return "Quantity must be greater than 0";

                  if (value > maxAllowed) {
                    setShipmentQtyWarnMsg(
                      `Warning: Entered quantity (${value.toLocaleString()}) exceeds available planned quantity (${maxAllowed.toLocaleString()}).`
                    );
                  }

                  return true; // Always allow submission
                },
              }}
              render={({ field: { onChange, value } }) => {
                const plannedQty = factoryOrderDetail?.plannedQty || 0;
                const existingPlannedQty =
                  orderDetailShipments?.reduce((sum: number, item: any) => {
                    return sum + (item?.shipmentQty || 0);
                  }, 0) || 0;

                const maxAllowed = Math.max(plannedQty - existingPlannedQty, 0);

                const handleNumberChange = (text: string) => {
                  const numericText = text.replace(/[^0-9]/g, "");
                  onChange(numericText === "" ? "" : Number(numericText));
                };

                const getPlaceholder = () =>
                  `Max: ${maxAllowed.toLocaleString()}`;

                return (
                  <View style={styles.field}>
                    <Text style={styles.label}>{t("shipmentQty")}</Text>
                    <TextInput
                      keyboardType="numeric"
                      style={styles.input}
                      value={value?.toLocaleString() || ""}
                      onChangeText={handleNumberChange}
                      placeholder={getPlaceholder()}
                    />
                    {errors.shipmentQty && (
                      <Text style={styles.error}>
                        {errors.shipmentQty.message}
                      </Text>
                    )}
                  </View>
                );
              }}
            />

            {shipmentQtyWarnMsg && (
              <Text style={{ color: "orange" }}>{shipmentQtyWarnMsg}</Text>
            )}

            <Controller
              control={control}
              name="containerNo"
              rules={{ required: "Container number is required" }}
              render={({ field: { onChange, value } }) => (
                <View style={styles.field}>
                  <Text style={styles.label}>{t("containerNo")}</Text>
                  <TextInput
                    keyboardType="default"
                    style={styles.input}
                    placeholder="Enter container number"
                    value={value}
                    onChangeText={onChange}
                  />
                  {errors.containerNo && (
                    <Text style={styles.error}>
                      {errors.containerNo.message}
                    </Text>
                  )}
                </View>
              )}
            />

            <Controller
              control={control}
              name="ETD"
              rules={{ required: "ETD is required" }}
              render={({ field: { onChange, value } }) =>
                renderDatePicker("ETD", value, onChange)
              }
            />

            <Controller
              control={control}
              name="duration"
              rules={{ required: "Duration is required" }}
              render={({ field: { onChange, value } }) => (
                <View style={styles.field}>
                  <Text style={styles.label}>{t("duration")}</Text>
                  <TextInput
                    keyboardType="default"
                    style={styles.input}
                    placeholder="Enter duration days"
                    value={value}
                    onChangeText={onChange}
                  />
                  {errors.duration && (
                    <Text style={styles.error}>{errors.duration.message}</Text>
                  )}
                </View>
              )}
            />

            <Controller
              control={control}
              name="ETA"
              disabled
              render={({ field: { onChange, value } }) =>
                renderDatePicker("ETA", value, onChange, true)
              }
            />

            <Controller
              control={control}
              name="remarks"
              render={({ field: { onChange, value } }) => (
                <View style={styles.field}>
                  <Text style={styles.label}>{t("remarks")}</Text>
                  <TextInput
                    multiline
                    numberOfLines={3}
                    style={[styles.input, { height: 80 }]}
                    placeholder="Enter remarks (optional)"
                    value={value}
                    onChangeText={onChange}
                  />
                </View>
              )}
            />

            <Button
              mode="contained"
              onPress={handleSubmit(onSubmit)}
              loading={loadingShipmentsAction}
              disabled={loadingShipmentsAction}
              style={styles.submitButton}
            >
              {t("submit")}
            </Button>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
};

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get("window");

const isSmallScreen = SCREEN_WIDTH < 400;
const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.25)",
    justifyContent: "center",
    alignItems: "center",
  },
  modalContainer: {
    backgroundColor: "#fff",
    padding: isSmallScreen ? 15 : 25,
    width:
      Platform.OS === "web"
        ? Math.min(500, SCREEN_WIDTH * 0.8)
        : SCREEN_WIDTH * 0.95,
    borderRadius: 10,
    maxHeight: SCREEN_HEIGHT * 0.9,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: isSmallScreen ? 8 : 10,
  },
  title: {
    fontSize: isSmallScreen ? 16 : 18,
    fontWeight: "600",
  },
  closeButton: {
    backgroundColor: "#1E88E5",
    borderRadius: 16,
    paddingHorizontal: isSmallScreen ? 8 : 10,
    paddingVertical: isSmallScreen ? 2 : 4,
  },
  closeButtonText: {
    color: "#fff",
    fontSize: isSmallScreen ? 14 : 16,
  },
  field: {
    marginBottom: isSmallScreen ? 10 : 12,
  },
  label: {
    fontWeight: "bold",
    marginBottom: 4,
    fontSize: isSmallScreen ? 13 : 14,
  },
  input: {
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 8,
    padding: isSmallScreen ? 8 : 10,
    fontSize: isSmallScreen ? 13 : 14,
  },
  error: {
    color: "red",
    fontSize: isSmallScreen ? 11 : 12,
    marginTop: 2,
  },
  submitButton: {
    marginTop: 20,
    backgroundColor: "#000",
    borderRadius: 8,
    paddingVertical: isSmallScreen ? 10 : 12,
  },
  datePicker: {
    backgroundColor: "#fff",
    borderRadius: 8,
  },
});
export default FactoryRunShipmentModal;
